import React, { useState, useEffect, useMemo } from 'react';
import { Typography, DatePicker, Button, Table, Space, message, Input, Tooltip, Grid, Card, List, Row, Col } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { getChiSoDefinitions, getChiSoValue } from '../services/chiSoService';
import dayjs, { Dayjs } from 'dayjs';
import { SyncOutlined, ReloadOutlined, FileExcelOutlined } from '@ant-design/icons';
import './ChiSoTongHop.css';

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

interface ChiSoRecord {
    maChiSo: number;
    tenChiSo: string;
    giaTri: string | null;
    thuTu: number;
    isLoading?: boolean;
    isChild?: boolean; // Thêm field để nhận biết chỉ số con
}

const ChiSoTongHop: React.FC = () => {
    const [masterData, setMasterData] = useState<ChiSoRecord[]>([]);
    const [loading, setLoading] = useState(false);
    const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([dayjs(), dayjs()]);
    const [nameSearch, setNameSearch] = useState('');

    // Load definitions only once on mount
    const fetchDefinitions = async () => {
        setLoading(true);
        try {
            const response = await getChiSoDefinitions();
            setMasterData(response.data.map((item: any) => ({ 
                ...item, 
                isLoading: false,
                isChild: item.tenChiSo && item.tenChiSo.trim().startsWith('+') // Nhận biết chỉ số con
            })));
        } catch (error) {
            console.error("Failed to fetch definitions:", error);
            message.error('Không thể tải danh mục chỉ số.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDefinitions();
    }, []);

    const fetchValue = async (record: ChiSoRecord) => {
        // Update loading state for this specific row
        setMasterData(prev => prev.map(item => 
            item.maChiSo === record.maChiSo ? { ...item, isLoading: true } : item
        ));

        try {
            const [fromDate, toDate] = dateRange;
            const fromDateStr = fromDate.startOf('day').format('YYYY-MM-DDTHH:mm:ss');
            const toDateStr = toDate.endOf('day').format('YYYY-MM-DDTHH:mm:ss');

            const response = await getChiSoValue(record.maChiSo, fromDateStr, toDateStr);
            
            // Update value and remove loading state
            setMasterData(prev => prev.map(item => 
                item.maChiSo === record.maChiSo 
                ? { ...item, giaTri: response.data.giaTri, isLoading: false } 
                : item
            ));
        } catch (error) {
            console.error(`Failed to fetch value for ${record.maChiSo}:`, error);
            message.error(`Lỗi lấy dữ liệu: ${record.tenChiSo}`);
            setMasterData(prev => prev.map(item => 
                item.maChiSo === record.maChiSo ? { ...item, isLoading: false } : item
            ));
        }
    };

    const handleFetchAll = async () => {
        const [fromDate, toDate] = dateRange;
        const fromDateStr = fromDate.startOf('day').format('YYYY-MM-DDTHH:mm:ss');
        const toDateStr = toDate.endOf('day').format('YYYY-MM-DDTHH:mm:ss');

        const itemsToFetch = filteredData;
        
        // Fetch sequentially to avoid overwhelming the server
        for (const item of itemsToFetch) {
            setMasterData(prev => prev.map(prevItem => 
                prevItem.maChiSo === item.maChiSo 
                ? { ...prevItem, isLoading: true } 
                : prevItem
            ));

            try {
                const response = await getChiSoValue(item.maChiSo, fromDateStr, toDateStr);
                setMasterData(prev => prev.map(prevItem => 
                    prevItem.maChiSo === item.maChiSo 
                    ? { ...prevItem, giaTri: response.data.giaTri, isLoading: false } 
                    : prevItem
                ));
            } catch (error) {
                console.error(`Failed to fetch value for ${item.maChiSo}:`, error);
                setMasterData(prev => prev.map(prevItem => 
                    prevItem.maChiSo === item.maChiSo 
                    ? { ...prevItem, isLoading: false } 
                    : prevItem
                ));
            }
        }
    };

    const handleExportExcel = () => {
        const itemsToExport = filteredData.filter(item => item.giaTri !== null);
        if (itemsToExport.length === 0) {
            message.warning('Không có dữ liệu để xuất. Vui lòng lấy dữ liệu trước.');
            return;
        }

        const [fromDate, toDate] = dateRange;
        const fromDateStr = fromDate.format('DD/MM/YYYY');
        const toDateStr = toDate.format('DD/MM/YYYY');

        let html = `
            <style>
                table { border-collapse: collapse; width: 100%; font-family: Arial, sans-serif; }
                th, td { border: 1px solid #000; padding: 5px; }
                th { background-color: #f2f2f2; font-weight: bold; }
                .child { color: #1890ff; font-style: italic; padding-left: 20px; }
                .parent { font-weight: bold; }
            </style>
            <table>
                <thead>
                    <tr>
                        <th colspan="3" style="font-size: 16px; text-align: center;">
                            DANH MỤC CHỈ SỐ TỔNG HỢP (${fromDateStr} - ${toDateStr})
                        </th>
                    </tr>
                    <tr>
                        <th>Thứ tự</th>
                        <th>Tên chỉ số</th>
                        <th>Giá trị</th>
                    </tr>
                </thead>
                <tbody>
        `;

        itemsToExport.forEach(item => {
            html += `
                <tr>
                    <td style="text-align: center;">${item.thuTu}</td>
                    <td class="${item.isChild ? 'child' : 'parent'}">${item.tenChiSo}</td>
                    <td style="text-align: right; font-weight: bold;">${item.giaTri}</td>
                </tr>
            `;
        });

        html += '</tbody></table>';

        const blob = new Blob(['\ufeff', html], { type: 'application/vnd.ms-excel' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `ChiSoTongHop_${fromDateStr}_${toDateStr}.xls`;
        link.click();
        URL.revokeObjectURL(url);
    };
    
    // Reset values when date changes (optional, or keep values and let user refresh)
    // Here we choose to just keep values but they might be stale. 
    // Let's clear values when date changes to indicate they need refresh.
    useEffect(() => {
        setMasterData(prev => prev.map(item => ({ ...item, giaTri: null })));
    }, [dateRange]);

    const filteredData = useMemo(() => {
        if (!nameSearch) {
            return masterData;
        }
        return masterData.filter(item =>
            item.tenChiSo.toLowerCase().includes(nameSearch.toLowerCase())
        );
    }, [masterData, nameSearch]);

    const screens = useBreakpoint();
    const isMobile = !screens.md;

    const renderMobileList = () => (
        <List
            dataSource={filteredData}
            loading={loading && masterData.length === 0}
            renderItem={(record: ChiSoRecord) => (
                <List.Item style={{ padding: '8px 0' }}>
                    <Card 
                        size="small" 
                        style={{ 
                            width: '100%',
                            borderRadius: 8, 
                            borderLeft: record.isChild ? '4px solid #1890ff' : '4px solid #262626',
                            backgroundColor: record.isChild ? '#f0f7ff' : '#ffffff',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                        }}
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <div style={{ flex: 1 }}>
                                <Space direction="vertical" size={0}>
                                    <Text type="secondary" style={{ fontSize: '11px' }}>Thứ tự: {record.thuTu}</Text>
                                    <Text 
                                        strong={!record.isChild}
                                        style={{ 
                                            fontSize: '14px',
                                            color: record.isChild ? '#1890ff' : '#262626',
                                            fontStyle: record.isChild ? 'italic' : 'normal',
                                            display: 'block',
                                            marginTop: 4
                                        }}
                                    >
                                        {record.tenChiSo}
                                    </Text>
                                </Space>
                            </div>
                            <div style={{ textAlign: 'right', marginLeft: 12 }}>
                                {record.isLoading ? (
                                    <SyncOutlined spin style={{ fontSize: '20px', color: '#1890ff' }} />
                                ) : (
                                    <Space direction="vertical" align="end" size={4}>
                                        <Text 
                                            strong 
                                            style={{ 
                                                fontSize: '18px', 
                                                color: record.isChild ? '#1890ff' : '#262626' 
                                            }}
                                        >
                                            {record.giaTri !== null ? record.giaTri : '-'}
                                        </Text>
                                        <Button 
                                            type="primary"
                                            ghost
                                            shape="circle"
                                            size="small"
                                            icon={<ReloadOutlined />} 
                                            onClick={() => fetchValue(record)} 
                                        />
                                    </Space>
                                )}
                            </div>
                        </div>
                    </Card>
                </List.Item>
            )}
        />
    );

    const columns: ColumnsType<ChiSoRecord> = [
        {
            title: 'Thứ Tự',
            dataIndex: 'thuTu',
            key: 'thuTu',
            width: 80,
            sorter: (a, b) => a.thuTu - b.thuTu,
            defaultSortOrder: 'ascend',
        },
        {
            title: 'Tên Chỉ Số',
            dataIndex: 'tenChiSo',
            key: 'tenChiSo',
            render: (text: string, record: ChiSoRecord) => {
                if (record.isChild) {
                    return (
                        <Typography.Text style={{ color: '#1890ff', paddingLeft: '20px', fontStyle: 'italic' }}>
                            {text}
                        </Typography.Text>
                    );
                }
                return (
                    <Typography.Text strong style={{ color: '#262626' }}>
                        {text}
                    </Typography.Text>
                );
            },
        },
        {
            title: 'Giá Trị',
            dataIndex: 'giaTri',
            key: 'giaTri',
            width: 120,
            render: (text, record: ChiSoRecord) => {
                if (record.isLoading) {
                    return <SyncOutlined spin />;
                }
                return (
                    <Space>
                        <Typography.Text 
                            strong={!record.isChild}
                            style={{ 
                                color: record.isChild ? '#1890ff' : '#262626',
                                fontStyle: record.isChild ? 'italic' : 'normal'
                            }}
                        >
                            {text !== null ? text : '-'}
                        </Typography.Text>
                        <Tooltip title="Lấy lại dữ liệu">
                            <Button 
                                type="text" 
                                icon={<ReloadOutlined />} 
                                size="small" 
                                onClick={() => fetchValue(record)} 
                            />
                        </Tooltip>
                    </Space>
                );
            }
        },
    ];

    const renderDesktopContent = () => {
        if (screens.lg && filteredData.length > 10) {
            const mid = Math.ceil(filteredData.length / 2);
            const leftData = filteredData.slice(0, mid);
            const rightData = filteredData.slice(mid);

            return (
                <Row gutter={24}>
                    <Col span={12}>
                        <Table 
                            columns={columns} 
                            dataSource={leftData} 
                            loading={loading && masterData.length === 0} 
                            rowKey="maChiSo" 
                            pagination={false} 
                            bordered
                            size="small"
                            rowClassName={(record: ChiSoRecord) => 
                                record.isChild ? 'child-row' : 'parent-row'
                            }
                        />
                    </Col>
                    <Col span={12}>
                        <Table 
                            columns={columns} 
                            dataSource={rightData} 
                            loading={loading && masterData.length === 0} 
                            rowKey="maChiSo" 
                            pagination={false} 
                            bordered
                            size="small"
                            rowClassName={(record: ChiSoRecord) => 
                                record.isChild ? 'child-row' : 'parent-row'
                            }
                        />
                    </Col>
                </Row>
            );
        }

        return (
            <Table 
                columns={columns} 
                dataSource={filteredData} 
                loading={loading && masterData.length === 0} 
                rowKey="maChiSo" 
                pagination={false} 
                bordered
                rowClassName={(record: ChiSoRecord) => 
                    record.isChild ? 'child-row' : 'parent-row'
                }
            />
        );
    };

    return (
        <div style={{ padding: '24px' }}>
            <Title level={4}>Danh mục chỉ số tổng hợp</Title>
            <Space style={{ marginBottom: 16, flexWrap: 'wrap' }}>
                <DatePicker
                    value={dateRange[0]}
                    onChange={(date) => {
                        if (date) setDateRange([date, dateRange[1]]);
                    }}
                    placeholder="Từ ngày"
                    format="DD/MM/YYYY"
                />
                <DatePicker
                    value={dateRange[1]}
                    onChange={(date) => {
                        if (date) setDateRange([dateRange[0], date]);
                    }}
                    placeholder="Đến ngày"
                    format="DD/MM/YYYY"
                />
                <Input
                    placeholder="Tìm theo tên chỉ số"
                    value={nameSearch}
                    onChange={(e) => setNameSearch(e.target.value)}
                    style={{ width: 200 }}
                />
                <Button 
                    icon={<FileExcelOutlined />} 
                    onClick={handleExportExcel}
                    style={{ backgroundColor: '#217346', color: 'white' }}
                >
                    Xuất Excel
                </Button>
                {/* <Button type="primary" onClick={handleFetchAll} loading={loading && masterData.length === 0}>
                    Lấy dữ liệu tất cả
                </Button> */}
            </Space>
            {isMobile ? renderMobileList() : renderDesktopContent()}
        </div>
    );
};

export default ChiSoTongHop;
